import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { partnerProxyKey } from '../../../core/services/partner-proxy';
import { getDefaultFmFilter } from './unpaid-payments-hub-products.config';
import { UnpaidPaymentsIsDummyService } from './unpaid-payments-is-dummy.service';

export interface UnifiedProductOption {
  key: string;
  label: string;
  serverName: string;
}

export interface UnifiedUnpaidFilters {
  search_string?: string;
  limit?: number;
  offset?: number;
  start_date?: string;
  end_date?: string;
  sub_status?: number;
  fm_filter?: number;
  fm_status_filter?: string;
  fm_partner_status?: string;
  fm_orp_filter?: number;
  fm_instant_claim?: number;
  /** FM admin: All | Business | Supporter */
  fm_share_type?: 'all' | 'business' | 'supporter';
  /** FM admin: All | Paid | UnPaid */
  fm_payment_status?: 'all' | 'paid' | 'unpaid';
  challenge_type?: string | number;
  is_vip?: number;
  is_urgent?: string | number;
  transfer_from?: 'BANK' | 'CARD';
  country_id?: number;
  lendee_share_type?: number;
  lendee_payment_status?: number;
  /** Full-list payable ids from list API (`contract_ids`, `papa_ids`, etc.). */
  aggregate_ids?: unknown;
}

export interface UnifiedUnpaidPayment {
  uspa_id?: number;
  pf_id?: number;
  dopa_id?: number;
  depa_id?: number;
  papa_id?: number;
  missing_payment_id?: number;
  dispute_id?: number;
  contract_id?: number;
  cr_id?: number;
  cashback_id?: number;
  charge_id?: number;
  payment_id?: number | string;
  email?: string;
  phone?: string;
  pay_to_user?: number;
  pay_to_doctor?: number;
  amount?: number;
  [key: string]: any;
}

interface ProductAdapterConfig {
  mode: 'simple' | 'fm';
  paymentType?: number;
  /** URL prefix prepended to relative API paths (e.g. `/adm/v1/`, `/admin/`). Include trailing slash. */
  apiPathPrefix?: string;
}

interface PartnerRuntimeSettings {
  access_token?: string;
  secret_key?: string;
  email?: string;
  baseUrl?: string;
  host?: string;
  country_id?: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class UnpaidPaymentsAdapterService {
  private readonly adapters: Record<string, ProductAdapterConfig> = {
    lendee: { mode: 'simple', paymentType: 9, apiPathPrefix: '/adm/v1/' },
    credee: { mode: 'fm', apiPathPrefix: '/admin/' },
    recuvery: { mode: 'fm', apiPathPrefix: '/admin/' },
    fm: { mode: 'fm', apiPathPrefix: '/admin/' },
  };

  constructor(
    private readonly apiService: ApiService,
    private readonly isDummyService: UnpaidPaymentsIsDummyService
  ) { }

  getProducts(): UnifiedProductOption[] {
    return [
      { key: 'lendee', label: 'Lendee', serverName: 'lendee' },
      { key: 'credee', label: 'Credee', serverName: 'credee' },
      { key: 'recuvery', label: 'Recuvery', serverName: 'recuvery' },
      { key: 'fm', label: 'FM', serverName: 'finance-mutual' },
    ];
  }

  listPayments(productKey: string, productServerName: string, filters: UnifiedUnpaidFilters): Observable<any> {
    const adapter = this.adapters[productKey];
    const request = adapter?.mode === 'fm' ? this.buildFmListRequest(productKey, filters) : this.buildSimpleListRequest(filters, adapter, productKey);
    const cleanedPayload = this.removeEmptyParams(request.payload);
    const runtime = this.getPartnerRuntimeFromSettings(productKey, productServerName);
    const commonOptions = {
      headers: this.buildPartnerAuthHeaders(productKey, runtime, filters),
      baseUrl: runtime.baseUrl,
      showErrorToaster: false,
      isUnpaidPayments: true,
    };

    // Legacy Recuvery and FM flows use GET for unpaid listing APIs.
    if (productKey === 'recuvery' || productKey === 'fm') {
      return this.apiService.productServerGet(
        productServerName as any,
        request.path,
        cleanedPayload,
        commonOptions
      );
    }

    return this.apiService.productServerPost(
      productServerName as any,
      request.path,
      cleanedPayload,
      commonOptions
    );
  }

  releasePayments(
    productKey: string,
    productServerName: string,
    selectedPayments: UnifiedUnpaidPayment[],
    filters: UnifiedUnpaidFilters
  ): Observable<any> {
    const request = this.buildReleaseRequest(productKey, selectedPayments, filters);
    return this.postReleaseRequest(productKey, productServerName, request, filters);
  }

  /** Pay all rows matching current FM filters (legacy `payAll()` — uses `status_filter`, not page rows). */
  releaseAllPayments(
    productKey: string,
    productServerName: string,
    filters: UnifiedUnpaidFilters
  ): Observable<any> {
    const request = this.buildReleaseAllRequest(productKey, filters);
    return this.postReleaseRequest(productKey, productServerName, request, filters);
  }

  private postReleaseRequest(
    productKey: string,
    productServerName: string,
    request: { path: string; payload: any },
    filters: UnifiedUnpaidFilters
  ): Observable<any> {
    const runtime = this.getPartnerRuntimeFromSettings(productKey, productServerName);
    return this.apiService.productServerPost(
      productServerName as any,
      request.path,
      request.payload,
      {
        headers: this.buildPartnerAuthHeaders(productKey, runtime, filters),
        baseUrl: runtime.baseUrl,
        isUnpaidPayments: true,
      }
    );
  }

  request<T>(
    productKey: string,
    productServerName: string,
    method: 'get' | 'post',
    path: string,
    payload: Record<string, any> = {},
    filters: UnifiedUnpaidFilters = {}
  ): Observable<T> {
    const runtime = this.getPartnerRuntimeFromSettings(productKey, productServerName);
    const options = {
      headers: this.buildPartnerAuthHeaders(productKey, runtime, filters),
      baseUrl: runtime.baseUrl,
      isUnpaidPayments: true,
    };
    const cleanedPayload = this.removeEmptyParams(payload);

    if (method === 'get') {
      return this.apiService.productServerGet(
        productServerName as any,
        path,
        cleanedPayload,
        options
      );
    }

    return this.apiService.productServerPost(
      productServerName as any,
      path,
      cleanedPayload,
      options
    );
  }

  private buildPartnerAuthHeaders(
    productKey: string,
    runtime: PartnerRuntimeSettings,
    filters?: UnifiedUnpaidFilters
  ): Record<string, string> {
    const headers: Record<string, string> = {};
    const normalizedKey = (productKey || '').toLowerCase();
    if (normalizedKey === 'recuvery' || normalizedKey === 'fm' || normalizedKey === 'credee') {
      headers['is_dummy'] = this.isDummyService.getStorageValue();
      headers['country_id'] = String(this.getDefaultCountryId());
      headers['timezone'] = String(
        sessionStorage.getItem('timezone') ?? localStorage.getItem('timezone') ?? '-700'
      );
    }

    return headers;
  }

  /** The partner's own access token from get-setting; never the Prism session token. */
  partnerAccessToken(productKey: string, productServerName: string): string {
    return this.getPartnerRuntimeFromSettings(productKey, productServerName).access_token || '';
  }

  /** Partner APIs (session / dynamic_routes) expect these on each request; Denefits unpaid uses HttpClient directly. */
  getPartnerAuthHeaders(
    productKey: string,
    productServerName: string,
    filters?: UnifiedUnpaidFilters
  ): Record<string, string> {
    const runtime = this.getPartnerRuntimeFromSettings(productKey, productServerName);
    // Through prism-new's partner proxy the credentials are attached server-side, never here.
    const partnerCredentials = partnerProxyKey(productServerName)
      ? {}
      : this.apiService.getProductPartnerAuthHeaders(productServerName, productKey === 'credee');
    return {
      ...partnerCredentials,
      ...this.buildPartnerAuthHeaders(productKey, runtime, filters),
    };
  }

  /**
   * Partner API host from `/auth/get-setting` (`sessionStorage.dynamic_routes`), merged with
   * product + settings (`baseUrl`, `base_url`, `url`, `product_base_url`).
   * Use this when calling partner backends with `HttpClient` directly (e.g. Denefits unpaid v2).
   */
  getPartnerApiBaseUrlFromSettings(productKey: string, productServerName: string): string | null {
    const runtime = this.getPartnerRuntimeFromSettings(productKey, productServerName);
    const raw = runtime.baseUrl;
    if (raw === undefined || raw === null) {
      return null;
    }
    const s = String(raw).trim();
    return s.length ? s : null;
  }

  private getPartnerRuntimeFromSettings(productKey: string, productServerName: string): PartnerRuntimeSettings {
    const empty: PartnerRuntimeSettings = {};
    const raw = sessionStorage.getItem('dynamic_routes');
    if (!raw) {
      return empty;
    }

    try {
      const routes = JSON.parse(raw);
      const normalizedServer = (productServerName || '').toLowerCase().trim();
      const normalizedKey = (productKey || '').toLowerCase().trim();
      const norm = (value: string) => value.replace(/-/g, '_');

      const match = (Array.isArray(routes) ? routes : []).find((entry: any) => {
        const product = entry?.product || {};
        const name = String(product?.name || '').toLowerCase().trim();
        const serverName = String(product?.server_name || product?.productServerName || '').toLowerCase().trim();
        const basePathSlug =
          String(product?.basePath || '')
            .toLowerCase()
            .split('/')
            .filter(Boolean)
            .pop() || '';

        const isFmLookup = normalizedKey === 'fm' || norm(normalizedServer) === 'finance_mutual';
        if (isFmLookup && (basePathSlug === 'finance_mutual' || name.includes('finance mutual'))) {
          return true;
        }

        return (
          name.includes(normalizedServer) ||
          name.includes(normalizedKey) ||
          serverName === normalizedServer ||
          serverName === normalizedKey ||
          basePathSlug === norm(normalizedServer) ||
          basePathSlug === norm(normalizedKey)
        );
      });

      const source = {
        ...(match?.product || {}),
        ...(match?.settings || {}),
        ...(match || {}),
      };
      const baseUrl =
        source.baseUrl ??
        source.base_url ??
        source.url ??
        source.product_base_url ??
        source.api_url ??
        source.apiUrl ??
        source.server_url ??
        source.serverUrl;
      const host = source.host ?? source.hostname ?? source.domain ?? this.getHostFromUrl(baseUrl);

      return {
        access_token: source.access_token ?? source.accessToken ?? source.authorization ?? source.auth_token ?? source.authToken,
        secret_key: source.secret_key ?? source.secretKey ?? source.secret ?? source.secretKeyValue,
        email: source.email ?? source.admin_email ?? source.adminEmail,
        baseUrl,
        host,
        country_id:
          source.country_id ??
          source.countryId ??
          sessionStorage.getItem('country_id') ??
          localStorage.getItem('country_id') ??
          '1',
      };
    } catch {
      return empty;
    }
  }

  private getHostFromUrl(rawUrl?: string): string {
    if (!rawUrl) {
      return '';
    }
    try {
      return new URL(rawUrl).host;
    } catch {
      return String(rawUrl)
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .trim();
    }
  }

  private normalizeSimpleUspaId(raw: unknown): number | null {
    if (raw === undefined || raw === null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private getApiPathPrefix(productKey?: string): string {
    return this.adapters[productKey ?? '']?.apiPathPrefix ?? '';
  }

  /** Prepends the product adapter prefix (e.g. `/admin/`) to a relative API path. */
  resolveProductApiPath(productKey: string, path: string): string {
    const prefix = this.getApiPathPrefix(productKey);
    if (!prefix) {
      return path;
    }
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${prefix}${normalizedPath}`;
  }

  getProductCountryId(productKey: string): number {
    const key = (productKey || '').toLowerCase();
    if (key === 'recuvery') {
      return this.getRecuveryCountryId();
    }
    if (key === 'credee') {
      return 0;
    }
    return this.getDefaultCountryId();
  }

  private removeEmptyParams<T extends Record<string, any>>(params: T): Partial<T> {
    return Object.entries(params).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc;
      }
      (acc as any)[key] = value;
      return acc;
    }, {} as Partial<T>);
  }

  getPaymentIdentifier(productKey: string, payment: UnifiedUnpaidPayment, filters: UnifiedUnpaidFilters): number | string | null {
    if (this.adapters[productKey]?.mode !== 'fm') {
      return payment.uspa_id ?? payment.payment_id ?? null;
    }
    const fmFilter = this.resolveFmFilter(productKey, filters);
    if (productKey === 'credee') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      if (fmFilter === 1 || fmFilter === 6) return payment.papa_id ?? payment.payment_id ?? null;
      if (fmFilter === 2) return payment.depa_id ?? payment.payment_id ?? null;
      if (fmFilter === 3) return payment.missing_payment_id ?? payment.payment_id ?? null;
      if (fmFilter === 4) return payment.dispute_id ?? payment.payment_id ?? null;
      if (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11 || fmFilter === 14 || fmFilter === 15) {
        return payment.charge_id ?? payment.dopa_id ?? payment.payment_id ?? null;
      }
      if (fmFilter === 9 || fmFilter === 12) {
        return statusFilter === '1'
          ? payment.papa_id ?? payment.payment_id ?? null
          : payment.dopa_id ?? payment.payment_id ?? null;
      }
      if (fmFilter === 13) return payment.cr_id ?? payment.payment_id ?? null;
      return payment.pf_id ?? payment.dopa_id ?? payment.payment_id ?? null;
    }
    if (productKey === 'fm') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      if (fmFilter === 1 || fmFilter === 6) return payment.papa_id ?? payment.payment_id ?? null;
      if (fmFilter === 2) return payment.depa_id ?? payment.payment_id ?? null;
      if (fmFilter === 3) return payment.missing_payment_id ?? payment.payment_id ?? null;
      if (fmFilter === 4) return payment.dispute_id ?? payment.payment_id ?? null;
      if (fmFilter === 5 || fmFilter === 8 || fmFilter === 11) {
        return payment.charge_id ?? payment.dopa_id ?? payment.payment_id ?? null;
      }
      if (fmFilter === 9 || fmFilter === 12) {
        return statusFilter === '1'
          ? payment.papa_id ?? payment.payment_id ?? null
          : payment.dopa_id ?? payment.payment_id ?? null;
      }
      if (fmFilter === 10) return payment.dopa_id ?? payment.charge_id ?? payment.payment_id ?? null;
      if (fmFilter === 13) return payment.cr_id ?? payment.payment_id ?? null;
      // Recurring cashback rows share contract-level payment_id; cashback_id is the row key.
      if (fmFilter === 14) return payment.cashback_id ?? null;
      if (fmFilter === 15) return payment.payment_id ?? payment.dopa_id ?? null;
      return payment.pf_id ?? payment.dopa_id ?? payment.payment_id ?? null;
    }
    if (productKey === 'recuvery') {
      if (fmFilter === 1 || fmFilter === 6 || (fmFilter === 4 && String(filters.fm_status_filter ?? '0') === '3')) {
        return payment.papa_id ?? payment.payment_id ?? null;
      }
      if (fmFilter === 2) return payment.depa_id ?? payment.payment_id ?? null;
      if (fmFilter === 3) return payment.missing_payment_id ?? payment.payment_id ?? null;
      if (fmFilter === 4) return payment.dispute_id ?? payment.payment_id ?? null;
      if (fmFilter === 5 || fmFilter === 8) {
        return payment.charge_id ?? payment.payment_id ?? null;
      }
      // Transfers / global payouts: ids differ by status filter (matches legacy selection behavior).
      if (fmFilter === 10 || fmFilter === 11 || fmFilter === 12 || fmFilter === 13) {
        if (String(filters.fm_status_filter ?? '0') === '1') {
          return payment.papa_id ?? payment.payment_id ?? null;
        }
        return payment.dopa_id ?? payment.payment_id ?? null;
      }
      return payment.pf_id ?? payment.dopa_id ?? payment.payment_id ?? null;
    }
    if (fmFilter === 1 || fmFilter === 6) return payment.papa_id ?? payment.payment_id ?? null;
    if (fmFilter === 2 || fmFilter === 5 || fmFilter === 8) return payment.depa_id ?? payment.payment_id ?? null;
    if (fmFilter === 3) return payment.contract_id ?? payment.payment_id ?? null;
    if (fmFilter === 4) return payment.dispute_id ?? payment.payment_id ?? null;
    if (fmFilter === 10) return payment.cr_id ?? payment.payment_id ?? null;
    if (fmFilter === 11) return payment.cashback_id ?? payment.payment_id ?? null;
    if (fmFilter === 12) return payment.charge_id ?? payment.payment_id ?? null;
    return payment.dopa_id ?? payment.pf_id ?? payment.payment_id ?? null;
  }

  private buildSimpleListRequest(filters: UnifiedUnpaidFilters, adapter?: ProductAdapterConfig, productKey?: string): { path: string; payload: any } {
    const apiPathPrefix = this.getApiPathPrefix(productKey);
    return {
      path: `${apiPathPrefix}payment/unpaid_payments_list`,
      payload: {
        search_string: filters.search_string || '',
        limit: Number(filters.limit ?? 10),
        offset: Number(filters.offset ?? 0),
        payment_type: adapter?.paymentType ?? 9,
        sub_status: Number(filters.sub_status ?? -1),
        start_date: filters.start_date,
        end_date: filters.end_date,
        is_dummy: Number(this.isDummyService.getStorageValue()),
      },
    };
  }

  private buildFmListRequest(productKey: string, filters: UnifiedUnpaidFilters): { path: string; payload: any } {
    const fmFilter = this.resolveFmFilter(productKey, filters);
    if (productKey === 'credee') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      const partnerStatus = String(filters.fm_partner_status ?? '0');
      const payload: any = {
        limit: Number(filters.limit ?? 10),
        offset: Number(filters.offset ?? 0),
        search_string: filters.search_string || '',
        filter: fmFilter,
        status_filter: statusFilter,
        partner_status: partnerStatus,
        is_orp: fmFilter === 3 ? Number(filters.fm_orp_filter ?? -1) : -1,
        is_orp_instant_claim: fmFilter === 3 ? Number(filters.fm_instant_claim ?? -1) : -1,
        country_id: Number(filters.country_id ?? 0),
        is_vip: Number(filters.is_vip ?? 0),
        is_urgent: String(filters.is_urgent ?? '0'),
        challenge_type: String(filters.challenge_type ?? '0'),
      };

      let path = 'unpaid_doctor_payments';
      if (fmFilter === 1) {
        path = 'unpaid_partner_payments';
      } else if (fmFilter === 2) {
        path = 'unpaid_paul_payments';
      } else if (fmFilter === 3) {
        path = 'unpaid_guaranteed_payments';
      } else if (fmFilter === 4) {
        if (statusFilter === '3') {
          path = 'unpaid_partner_disputed_payments';
          delete payload.status_filter;
        } else {
          path = 'unpaid_disputed_payments';
        }
      } else if (fmFilter === 5 || fmFilter === 8 || fmFilter === 14 || fmFilter === 15) {
        path = 'unpaid_doctor_ach_payments';
        payload.stripe_status = fmFilter === 14 ? 22 : fmFilter === 15 ? 23 : fmFilter === 5 ? 17 : 18;
      } else if (fmFilter === 6) {
        path = 'unpaid_partner_ach_payments';
      } else if (fmFilter === 7) {
        path = 'unpaid_list_contract_difference';
        payload.status = 3;
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 9) {
        if (statusFilter === '1') {
          path = 'unpaid_partner_cashadjustment_payments';
        } else {
          path = 'unpaid_doctor_cashadjustment_payments';
        }
      } else if (fmFilter === 10) {
        path = 'unpaid_pp_ar_payments';
        payload.type = statusFilter === '1' ? 47 : statusFilter === '0' ? 1 : 46;
      } else if (fmFilter === 11) {
        path = 'unpaid_doctor_payments_in_house';
        payload.stripe_status = 20;
      } else if (fmFilter === 12) {
        path = 'get_doctor_unpaid_transfers';
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 13) {
        path = 'unpaid_doctor_banner_challenge';
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 16) {
        const beneficiaryType = Number(filters.lendee_share_type ?? 0);
        return {
          path: `${this.getApiPathPrefix(productKey)}get_lendee_unpaid_list`,
          payload: {
            access_token: this.partnerAccessToken(productKey, ''),
            limit: Number(filters.limit ?? 10),
            offset: Number(filters.offset ?? 0),
            search: filters.search_string || '',
            start_date: filters.start_date || '',
            end_date: filters.end_date || '',
            ...(beneficiaryType !== 0 ? { beneficiary_type: String(beneficiaryType) } : {}),
            payment_status: String(filters.lendee_payment_status ?? 0),
            is_urgent: String(filters.is_urgent ?? 0),
            country_id: Number(filters.country_id ?? 0),
            is_dummy: Number(this.isDummyService.getStorageValue()),
          },
        };
      }

      if (fmFilter !== 1 && fmFilter !== 6) {
        delete payload.partner_status;
      }
      if (fmFilter !== 3) {
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      }
      if (fmFilter !== 13) {
        delete payload.challenge_type;
      }
      if (fmFilter !== 4 && payload.status_filter === '3') {
        payload.status_filter = '0';
      }

      return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload };
    }
    if (productKey === 'fm') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      const partnerStatus = String(filters.fm_partner_status ?? '0');
      const payload: any = {
        limit: Number(filters.limit ?? 10),
        offset: Number(filters.offset ?? 0),
        search_string: filters.search_string || '',
        filter: fmFilter,
        is_orp: fmFilter === 3 ? Number(filters.fm_orp_filter ?? -1) : -1,
        is_orp_instant_claim: fmFilter === 3 ? Number(filters.fm_instant_claim ?? -1) : -1,
        country_id: Number(filters.country_id ?? 0),
        is_vip: Number(filters.is_vip ?? 0),
        is_urgent: String(filters.is_urgent ?? '0'),
      };

      // Match legacy FM unpaid list: status_filter is omitted for several filters (e.g. Paul = 2).
      if (
        fmFilter === 0 ||
        fmFilter === 1 ||
        fmFilter === 3 ||
        fmFilter === 4 ||
        fmFilter === 5 ||
        fmFilter === 6 ||
        fmFilter === 7 ||
        fmFilter === 8 ||
        fmFilter === 11 ||
        fmFilter === 12
      ) {
        payload.status_filter = statusFilter;
      }
      if (fmFilter === 1 || fmFilter === 6) {
        payload.partner_status = partnerStatus;
      }
      if (fmFilter === 13 || fmFilter === 14) {
        payload.challenge_type = String(filters.challenge_type ?? '0');
      }

      let path = 'finance/unpaid_doctor_payments';
      if (fmFilter === 1) {
        path = 'finance/unpaid_partner_payments';
      } else if (fmFilter === 2) {
        path = 'finance/unpaid_paul_payments';
      } else if (fmFilter === 3) {
        path = 'finance/unpaid_guaranteed_payments';
      } else if (fmFilter === 4) {
        if (statusFilter === '3') {
          path = 'finance/unpaid_partner_disputed_payments';
          delete payload.status_filter;
        } else {
          path = 'finance/unpaid_disputed_payments';
        }
      } else if (fmFilter === 5 || fmFilter === 8) {
        path = 'finance/unpaid_doctor_ach_payments';
        payload.stripe_status = fmFilter === 5 ? 17 : 18;
      } else if (fmFilter === 6) {
        path = 'finance/unpaid_partner_ach_payments';
      } else if (fmFilter === 7) {
        path = 'finance/unpaid_list_contract_difference';
        payload.status = 3;
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 9) {
        if (statusFilter === '1') {
          path = 'finance/unpaid_partner_cashadjustment_payments';
        } else {
          path = 'finance/unpaid_doctor_cashadjustment_payments';
        }
      } else if (fmFilter === 10) {
        path = 'finance/unpaid_pp_ar_payments';
        payload.type = statusFilter === '1' ? 47 : statusFilter === '0' ? 1 : 46;
      } else if (fmFilter === 11) {
        path = 'finance/unpaid_doctor_payments_in_house';
        payload.stripe_status = 20;
      } else if (fmFilter === 12) {
        path = 'finance/get_doctor_unpaid_transfers';
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 13) {
        path = 'finance/unpaid_doctor_banner_challenge';
        delete payload.filter;
      } else if (fmFilter === 14) {
        path = 'finance/unpaid_recurring_cashback';
        delete payload.filter;
      } else if (fmFilter === 15) {
        const countryId = Number(filters.country_id ?? this.getProductCountryId(productKey));
        return {
          path: `${this.getApiPathPrefix(productKey)}unpaid_payments/get_lendee_unpaid_list`,
          payload: {
            page: Math.floor(Number(filters.offset ?? 0) / Number(filters.limit ?? 10)) + 1,
            limit: Number(filters.limit ?? 10),
            offset: Number(filters.offset ?? 0),
            share_type: Number(filters.lendee_share_type ?? 0),
            payout_status: Number(filters.lendee_payment_status ?? 0),
            country_id: countryId,
          },
        };
      }

      if (fmFilter !== 4 && payload.status_filter === '3') {
        payload.status_filter = '0';
      }

      const fmShareType = filters.fm_share_type;
      const fmPaymentStatus = filters.fm_payment_status;
      if (fmShareType != null) {
        payload.share_type = fmShareType;
      }
      if (fmPaymentStatus != null && fmPaymentStatus !== 'all') {
        payload.payment_status = fmPaymentStatus;
      }

      return {
        path: `${this.getApiPathPrefix(productKey)}${path}`,
        payload: this.normalizeFmListQueryPayload(payload),
      };
    }

    if (productKey === 'recuvery') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      const partnerStatus = String(filters.fm_partner_status ?? '0');
      const payload: any = {
        limit: Number(filters.limit ?? 10),
        offset: Number(filters.offset ?? 0),
        search_string: filters.search_string || '',
        start_date: filters.start_date || '',
        end_date: filters.end_date || '',
        filter: fmFilter,
        status_filter: statusFilter,
        partner_status: partnerStatus,
        is_vip: Number(filters.is_vip ?? 0) === 1 ? 1 : 0,
        is_urgent: String(filters.is_urgent ?? '0'),
        country_id: this.getRecuveryCountryId(),
        is_orp: fmFilter === 3 ? Number(filters.fm_orp_filter ?? -1) : -1,
        is_orp_instant_claim: fmFilter === 3 ? Number(filters.fm_instant_claim ?? -1) : -1,
      };
      const apiPathPrefix = this.getApiPathPrefix(productKey);
      let path = 'finance/unpaid_doctor_payments';
      if (fmFilter === 1) { // Unpaid Partner Payments
        path = 'finance/unpaid_partner_payments';
      } else if (fmFilter === 2) { // Unpaid Paul Payments
        path = 'finance/unpaid_paul_payments';
      } else if (fmFilter === 3) { // Unpaid Guaranteed Payments
        path = 'finance/unpaid_guaranteed_payments';
      } else if (fmFilter === 4) { // Unpaid Disputed Payments
        if (statusFilter === '3') {
          path = 'finance/unpaid_partner_disputed_payments';
          delete payload.status_filter;
        } else {
          path = 'finance/unpaid_disputed_payments';
        }
      } else if (fmFilter === 5 || fmFilter === 8) { // ACH Doctor Payments
        path = 'finance/unpaid_doctor_ach_payments';
        payload.stripe_status = fmFilter === 5 ? 17 : 18;
      } else if (fmFilter === 6) { // Unpaid Partner ACH Payments
        path = 'finance/unpaid_partner_ach_payments';
      } else if (fmFilter === 7) { // Bank Failed Payments
        path = 'finance/unpaid_list_contract_difference';
        payload.status = 3;
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 9) { // Unpaid Cash Adjustment Payments
        if (statusFilter === '1') {
          path = 'finance/unpaid_partner_cashadjustment_payments';
        } else {
          path = 'finance/unpaid_doctor_cashadjustment_payments';
        }
      } else if (fmFilter === 10) { // Bank Not Added - Transfer Disabled
        path = 'finance/get_doctor_unpaid_transfers';
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 11) { // Lendee Supporter
        path = 'finance/get_dlc_unpaid_transfers';
        payload.dlc_type = this.getDlcTypeFromFmFilter(fmFilter);
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      } else if (fmFilter === 12 || fmFilter === 13) { // Credee / Denefits
        path = 'finance/get_dlc_unpaid_transfers';
        payload.dlc_type = this.getDlcTypeFromFmFilter(fmFilter);
        delete payload.filter;
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      }

      if (fmFilter !== 1 && fmFilter !== 6) {
        delete payload.partner_status;
      }
      if (fmFilter !== 3) {
        delete payload.is_orp;
        delete payload.is_orp_instant_claim;
      }
      if (fmFilter !== 4 && payload.status_filter === '3') {
        payload.status_filter = '0';
      }

      return { path: `${apiPathPrefix}${path}`, payload };
    }

    const payload: any = {
      limit: Number(filters.limit ?? 10),
      offset: Number(filters.offset ?? 0),
      search_string: filters.search_string || '',
      filter: fmFilter,
      status_filter: String(filters.fm_status_filter ?? '0'),
      partner_status: String(filters.fm_partner_status ?? '0'),
      is_orp: Number(filters.fm_orp_filter ?? -1),
      is_orp_instant_claim: Number(filters.fm_instant_claim ?? -1),
    };
    const apiPathPrefix = this.getApiPathPrefix(productKey);
    let path = 'finance/unpaid_doctor_payments';
    if (fmFilter === 1) path = 'finance/unpaid_partner_payments';
    else if (fmFilter === 2) path = 'finance/unpaid_paul_payments';
    else if (fmFilter === 3) path = 'finance/unpaid_guaranteed_payments';
    else if (fmFilter === 4) path = 'finance/unpaid_disputed_payments';
    else if (fmFilter === 5 || fmFilter === 8) {
      path = 'finance/unpaid_doctor_ach_payments';
      payload.stripe_status = fmFilter === 5 ? 17 : 18;
    } else if (fmFilter === 6) {
      path = 'finance/unpaid_partner_ach_payments';
    } else if (fmFilter === 7) {
      path = 'finance/bank_failed_unpaid_payments';
      payload.status = 3;
      delete payload.filter;
    } else if (fmFilter === 10) {
      path = 'finance/unpaid_recovery_payments';
      payload.type = 0;
      delete payload.filter;
    } else if (fmFilter === 11) {
      path = 'finance/unpaid_cashback_payments';
      payload.type = 0;
      delete payload.filter;
    } else if (fmFilter === 12) {
      path = 'finance/unpaid_collection_charges';
      payload.type = 0;
      delete payload.filter;
      delete payload.is_orp;
      delete payload.is_orp_instant_claim;
    }

    return { path: `${apiPathPrefix}${path}`, payload };
  }

  private buildReleaseRequest(
    productKey: string,
    selectedPayments: UnifiedUnpaidPayment[],
    filters: UnifiedUnpaidFilters
  ): { path: string; payload: any } {
    const adapter = this.adapters[productKey];
    if (adapter?.mode !== 'fm') {
      const ids = selectedPayments
        .map((p) => this.normalizeSimpleUspaId(p.uspa_id ?? p.payment_id))
        .filter((v): v is number => v !== null);
      const uspa_id =
        ids.length === 0
          ? null
          : ids.length === 1
            ? ids[0].toString()
            : ids.map((id) => id.toString());
      return {
        path: `${this.getApiPathPrefix(productKey)}payment/pay_unpaid_payment_to_user`,
        payload: { uspa_id },
      };
    }

    const fmFilter = this.resolveFmFilter(productKey, filters);
    const payload: any = {};
    let path = 'finance/ach_doctor_pay';

    if (productKey === 'credee') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      if (fmFilter === 0) {
        payload.dopa_id = this.concatCredeePaymentField(selectedPayments, 'dopa_id');
        payload.contract_id = this.concatCredeePaymentField(selectedPayments, 'contract_id');
        path = 'pay_to_doctors';
      } else if (fmFilter === 1) {
        payload.papa_id = this.concatCredeePaymentField(selectedPayments, 'papa_id');
        path = 'pay_to_partners';
      } else if (fmFilter === 2) {
        payload.depa_id = this.concatCredeePaymentField(selectedPayments, 'depa_id');
        path = 'pay_to_paul';
      } else if (fmFilter === 3) {
        payload.missing_payment_id = this.concatCredeePaymentField(selectedPayments, 'missing_payment_id');
        payload.is_orp = Number(filters.fm_orp_filter ?? -1);
        payload.is_orp_instant_claim = Number(filters.fm_instant_claim ?? -1);
        path = 'pay_guaranteed_payments';
      } else if (fmFilter === 4) {
        if (statusFilter === '3') {
          payload.papa_id = this.concatCredeePaymentField(selectedPayments, 'papa_id');
          payload.status_filter = 3;
          path = 'pay_to_partners';
        } else {
          payload.dispute_id = this.concatCredeePaymentField(selectedPayments, 'dispute_id');
          path = 'pay_won_disputes';
        }
      } else if (fmFilter === 5) {
        payload.dopa_id = this.concatCredeePaymentField(selectedPayments, 'dopa_id');
        payload.contract_id = this.concatCredeePaymentField(selectedPayments, 'contract_id');
        payload.stripe_status = 17;
        payload.transfer_from = filters.transfer_from;
        path = 'pay_ach_payments_to_doctors_v2';
      } else if (fmFilter === 6) {
        payload.papa_id = this.concatCredeePaymentField(selectedPayments, 'papa_id');
        payload.transfer_from = filters.transfer_from;
        path = 'pay_ach_payments_to_partners';
      } else if (fmFilter === 7) {
        payload.contract_id = this.concatCredeePaymentField(selectedPayments, 'contract_id');
        path = 'pay_contract_diff_amount';
      } else if (fmFilter === 8 || fmFilter === 10 || fmFilter === 11 || fmFilter === 14 || fmFilter === 15) {
        payload.dopa_id = this.concatCredeePaymentField(selectedPayments, 'dopa_id');
        payload.contract_id = this.concatCredeePaymentField(selectedPayments, 'contract_id');
        payload.stripe_status =
          fmFilter === 14 ? 22 :
            fmFilter === 15 ? 23 :
              fmFilter === 10 ? 17 :
                fmFilter === 11 ? 20 : 18;
        payload.transfer_from = filters.transfer_from;
        path = 'pay_ach_payments_to_doctors';
      } else if (fmFilter === 9) {
        if (statusFilter === '1') {
          payload.papa_id = this.concatCredeePaymentField(selectedPayments, 'papa_id');
          path = 'pay_cashadjust_payments_to_partners';
        } else {
          payload.dopa_id = this.concatCredeePaymentField(selectedPayments, 'dopa_id');
          payload.contract_id = this.concatCredeePaymentField(selectedPayments, 'contract_id');
          path = 'pay_cashadjust_payments_to_doctors';
        }
      } else if (fmFilter === 12) {
        if (statusFilter === '1') {
          payload.papa_id = this.concatCredeePaymentField(selectedPayments, 'papa_id');
        } else {
          payload.dopa_id = this.concatCredeePaymentField(selectedPayments, 'dopa_id');
        }
        path = 'pay_doctor_unpaid_transfers';
      } else if (fmFilter === 13) {
        const crIds = this.concatCredeePaymentField(selectedPayments, 'cr_id');
        if (selectedPayments.length > 1) {
          payload.cr_id_arr = crIds;
        } else {
          payload.cr_id = crIds;
        }
        path = 'pay_doctor_banner_challenge';
      }

      payload.country_id = Number(filters.country_id ?? 0);
      return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload };
    }

    if (productKey === 'fm') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      if (fmFilter === 0) {
        this.setFmDopaAndContractPayload(payload, selectedPayments);
        path = 'finance/pay_to_doctors';
      } else if (fmFilter === 1) {
        this.setFmPapaPayload(payload, selectedPayments);
        path = 'finance/pay_to_partners';
      } else if (fmFilter === 2) {
        payload.depa_id = this.concatFmPaymentField(selectedPayments, 'depa_id');
        path = 'finance/pay_to_paul';
      } else if (fmFilter === 3) {
        payload.missing_payment_id = this.concatFmPaymentField(selectedPayments, 'missing_payment_id');
        payload.is_orp = Number(filters.fm_orp_filter ?? -1);
        payload.is_orp_instant_claim = Number(filters.fm_instant_claim ?? -1);
        path = 'finance/pay_guaranteed_payments';
      } else if (fmFilter === 4) {
        if (statusFilter === '3') {
          this.setFmPapaPayload(payload, selectedPayments);
          payload.status_filter = 3;
          path = 'finance/pay_to_partners';
        } else {
          payload.dispute_id = this.concatFmPaymentField(selectedPayments, 'dispute_id');
          path = 'finance/pay_won_disputes';
        }
      } else if (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11) {
        this.setFmDopaAndContractPayload(payload, selectedPayments);
        payload.stripe_status = fmFilter === 5 || fmFilter === 10 ? 17 : fmFilter === 11 ? 20 : 18;
        payload.transfer_from = filters.transfer_from;
        path = 'finance/pay_ach_payments_to_doctors_v2';
      } else if (fmFilter === 6) {
        this.setFmPapaPayload(payload, selectedPayments);
        payload.transfer_from = filters.transfer_from;
        path = 'finance/pay_ach_payments_to_partners';
      } else if (fmFilter === 7) {
        this.setFmContractPayload(payload, selectedPayments);
        path = 'finance/pay_contract_diff_amount';
      } else if (fmFilter === 9) {
        if (statusFilter === '1') {
          this.setFmPapaPayload(payload, selectedPayments);
          path = 'finance/pay_cashadjust_payments_to_partners';
        } else {
          this.setFmDopaAndContractPayload(payload, selectedPayments);
          path = 'finance/pay_cashadjust_payments_to_doctors';
        }
      } else if (fmFilter === 12) {
        this.setFmDopaPayload(payload, selectedPayments);
        path = 'finance/pay_doctor_unpaid_transfers';
      } else if (fmFilter === 13) {
        if (selectedPayments.length > 1) {
          payload.cr_id_arr = selectedPayments.map((p) => p.cr_id).filter((id) => id != null);
        } else {
          payload.cr_id = selectedPayments[0]?.cr_id;
        }
        path = 'finance/pay_doctor_banner_challenge';
      } else if (fmFilter === 14) {
        if (selectedPayments.length > 1) {
          payload.cashback_id_arr = selectedPayments.map((p) => p.cashback_id).filter((id) => id != null);
        } else {
          payload.cashback_id = selectedPayments[0]?.cashback_id;
        }
        path = 'finance/pay_recurring_cashback';
      }

      return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload };
    }

    if (productKey === 'recuvery') {
      const statusFilter = String(filters.fm_status_filter ?? '0');
      payload.country_id = this.getRecuveryCountryId();
      if (fmFilter === 0) { // Doctor Payments 
        payload.dopa_id = selectedPayments.map((p) => p.dopa_id).filter(Boolean);
        payload.contract_id = selectedPayments.map((p) => p.contract_id).filter(Boolean);
        path = 'finance/pay_to_doctors';
      } else if (fmFilter === 1 || fmFilter === 6) { // Partner Payments / Partner ACH
        payload.papa_id = selectedPayments.map((p) => p.papa_id).filter(Boolean);
        path = 'finance/pay_to_partners';
      } else if (fmFilter === 2) { // Paul Payments
        payload.depa_id = selectedPayments.map((p) => p.depa_id).filter(Boolean);
        path = 'finance/pay_to_paul';
      } else if (fmFilter === 3) { // Guaranteed Payments
        payload.missing_payment_id = selectedPayments.map((p) => p.missing_payment_id).filter(Boolean);
        payload.is_orp = Number(filters.fm_orp_filter ?? -1);
        payload.is_orp_instant_claim = Number(filters.fm_instant_claim ?? -1);
        path = 'finance/pay_guaranteed_payments';
      } else if (fmFilter === 4) { // Disputed Payments
        if (statusFilter === '3') {
          payload.papa_id = selectedPayments.map((p) => p.papa_id).filter(Boolean);
          payload.status_filter = 3;
          path = 'finance/pay_to_partners';
        } else {
          payload.dispute_id = selectedPayments.map((p) => p.dispute_id).filter(Boolean);
          path = 'finance/pay_won_disputes';
        }
      } else if (fmFilter === 5) { // ACH Doctor Payments (v2)
        this.setFmDopaAndContractPayload(payload, selectedPayments);
        payload.stripe_status = 17;
        payload.transfer_from = filters.transfer_from;
        path = 'finance/pay_ach_payments_to_doctors_v2';
      } else if (fmFilter === 6) { // Unpaid Partner ACH Payments
        payload.papa_id = selectedPayments.map((p) => p.papa_id).filter(Boolean);
        path = 'finance/pay_ach_payments_to_partners';
      } else if (fmFilter === 7) { // Bank Failed Payments
        payload.contract_id = selectedPayments.map((p) => p.contract_id).filter(Boolean);
        path = 'finance/pay_contract_diff_amount';
      } else if (fmFilter === 8) { // ACH Doctor Payments (v1)
        this.setFmDopaAndContractPayload(payload, selectedPayments);
        payload.stripe_status = 18;
        payload.transfer_from = filters.transfer_from;
        path = 'finance/pay_ach_payments_to_doctors';
      } else if (fmFilter === 9) { // Cash Adjustment Payments
        if (statusFilter === '1') {
          this.setFmPapaPayload(payload, selectedPayments);
          path = 'finance/pay_cashadjust_payments_to_partners';
        } else {
          this.setFmDopaAndContractPayload(payload, selectedPayments);
          path = 'finance/pay_cashadjust_payments_to_doctors';
        }
      } else if (fmFilter === 10) { // Bank Not Added - Transfer Disabled
        if (statusFilter === '1') {
          this.setFmPapaPayload(payload, selectedPayments);
        } else {
          this.setFmDopaPayload(payload, selectedPayments);
        }
        path = 'finance/pay_doctor_unpaid_transfers';
      } else if (fmFilter === 11 || fmFilter === 12 || fmFilter === 13) { // Global Payouts
        payload.dlc_type = this.getDlcTypeFromFmFilter(fmFilter);
        if (statusFilter === '1') {
          this.setRecuveryPapaPayload(payload, selectedPayments);
        } else {
          this.setRecuveryDopaPayload(payload, selectedPayments);
        }
        path = 'finance/send_money_to_unpiad_dlc';
      }

      return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload };
    }

    if (fmFilter === 0 || fmFilter === 9) {
      payload.dopa_id = selectedPayments.map((p) => p.dopa_id).filter(Boolean);
      path = 'finance/ach_doctor_pay';
    } else if (fmFilter === 1 || fmFilter === 6) {
      payload.papa_id = selectedPayments.map((p) => p.papa_id).filter(Boolean);
      path = 'finance/ach_partner_pay';
    } else if (fmFilter === 2 || fmFilter === 5 || fmFilter === 8) {
      payload.depa_id = selectedPayments.map((p) => p.depa_id).filter(Boolean);
      path = 'finance/ach_doctor_pay';
    } else if (fmFilter === 3) {
      payload.contract_id = selectedPayments.map((p) => p.contract_id).filter(Boolean);
      path = 'finance/ach_guaranteed_pay';
    } else if (fmFilter === 4) {
      payload.dispute_id = selectedPayments.map((p) => p.dispute_id).filter(Boolean);
      path = 'finance/dispute_pay';
    } else if (fmFilter === 10) {
      payload.cr_id = selectedPayments.map((p) => p.cr_id).filter(Boolean);
      path = 'finance/pay_recovery';
    } else if (fmFilter === 11) {
      payload.cashback_id = selectedPayments.map((p) => p.cashback_id).filter(Boolean);
      path = 'finance/pay_cashback';
    } else if (fmFilter === 12) {
      payload.charge_id = selectedPayments.map((p) => p.charge_id).filter(Boolean);
      path = 'finance/pay_collection_charge';
    }

    return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload };
  }

  /** Legacy FM/Credee/Recuvery bulk pay — full filtered listing via `status_filter` (and aggregate ids when searched). */
  private buildReleaseAllRequest(
    productKey: string,
    filters: UnifiedUnpaidFilters
  ): { path: string; payload: any } {
    const fmFilter = this.resolveFmFilter(productKey, filters);
    const statusFilter = String(filters.fm_status_filter ?? '0');
    const aggregateIds = filters.aggregate_ids;
    const isSearched = Boolean(String(filters.search_string ?? '').trim());
    const payload: any = { status_filter: statusFilter };
    let path = '';

    if (isSearched && aggregateIds != null) {
      this.applyPayAllAggregateIds(payload, productKey, fmFilter, statusFilter, aggregateIds);
    }

    if (productKey === 'credee') {
      path = this.resolveCredeePayAllPath(fmFilter, statusFilter, payload, filters);
      payload.country_id = Number(filters.country_id ?? 0);
    } else if (productKey === 'fm') {
      path = this.resolveFmPayAllPath(fmFilter, statusFilter, payload, filters);
    } else if (productKey === 'recuvery') {
      path = this.resolveRecuveryPayAllPath(fmFilter, statusFilter, payload, filters);
      payload.country_id = this.getRecuveryCountryId();
    } else {
      path = 'finance/ach_doctor_pay';
    }

    this.applyPayAllCommonPayload(payload, productKey, fmFilter, statusFilter, aggregateIds, filters);
    return { path: `${this.getApiPathPrefix(productKey)}${path}`, payload: this.removeEmptyParams(payload) };
  }

  private applyPayAllAggregateIds(
    payload: Record<string, unknown>,
    productKey: string,
    fmFilter: number,
    statusFilter: string,
    aggregateIds: unknown
  ): void {
    if (fmFilter === 0) {
      payload['contract_id'] = aggregateIds;
      return;
    }
    if (fmFilter === 1 || fmFilter === 6) {
      payload['papa_id'] = aggregateIds;
      return;
    }
    if (fmFilter === 2) {
      payload['depa_id'] = aggregateIds;
      return;
    }
    if (fmFilter === 3) {
      payload['missing_payment_id'] = aggregateIds;
      return;
    }
    if (fmFilter === 4) {
      payload['dispute_id'] = aggregateIds;
      return;
    }
    if (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11 || fmFilter === 14 || fmFilter === 15) {
      payload['contract_id'] = aggregateIds;
      payload['stripe_status'] = this.resolvePayAllStripeStatus(productKey, fmFilter);
      return;
    }
    if (fmFilter === 9) {
      if (statusFilter !== '1') {
        payload['dopa_id'] = aggregateIds;
      } else {
        payload['papa_id'] = aggregateIds;
      }
    }
  }

  private applyPayAllCommonPayload(
    payload: Record<string, unknown>,
    productKey: string,
    fmFilter: number,
    statusFilter: string,
    aggregateIds: unknown,
    filters: UnifiedUnpaidFilters
  ): void {
    if (fmFilter === 1 || fmFilter === 6) {
      if (aggregateIds != null) {
        payload['papa_id'] = aggregateIds;
      }
      payload['transfer_from'] = filters.transfer_from ?? 'CARD';
    }

    if (fmFilter !== 4 && payload['status_filter'] === '3') {
      payload['status_filter'] = '0';
    }

    if (fmFilter === 3) {
      payload['is_orp'] = Number(filters.fm_orp_filter ?? -1);
      payload['is_orp_instant_claim'] = Number(filters.fm_instant_claim ?? -1);
    }

    if (fmFilter === 7) {
      payload['status'] = 3;
    }

    if (
      productKey === 'credee' &&
      (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11 || fmFilter === 14 || fmFilter === 15)
    ) {
      payload['stripe_status'] = this.resolvePayAllStripeStatus(productKey, fmFilter);
    }

    if (productKey === 'fm' && (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11)) {
      payload['stripe_status'] = this.resolvePayAllStripeStatus(productKey, fmFilter);
    }

    if (productKey === 'recuvery' && (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11)) {
      payload['stripe_status'] = this.resolvePayAllStripeStatus(productKey, fmFilter);
    }

    if (productKey === 'credee' && fmFilter === 13) {
      payload['cr_id_all'] = 1;
    }

    if (productKey === 'fm' && fmFilter === 13) {
      payload['cr_id_all'] = 1;
    }

    if (productKey === 'fm' && fmFilter === 14) {
      payload['cashback_id_all'] = 1;
    }

    if (productKey === 'fm' && fmFilter === 12) {
      payload['send_all'] = 1;
    }
  }

  private resolvePayAllStripeStatus(productKey: string, fmFilter: number): number {
    if (productKey === 'credee') {
      if (fmFilter === 14) return 22;
      if (fmFilter === 15) return 23;
      if (fmFilter === 5 || fmFilter === 10) return 17;
      if (fmFilter === 11) return 20;
      return 18;
    }
    if (fmFilter === 5 || fmFilter === 10) return 17;
    if (fmFilter === 11) return 20;
    return 18;
  }

  private resolveCredeePayAllPath(
    fmFilter: number,
    statusFilter: string,
    payload: Record<string, unknown>,
    filters: UnifiedUnpaidFilters
  ): string {
    if (fmFilter === 0) return 'pay_to_doctors';
    if (fmFilter === 1) return 'pay_to_partners';
    if (fmFilter === 2) return 'pay_to_paul';
    if (fmFilter === 3) return 'pay_guaranteed_payments';
    if (fmFilter === 4) {
      if (statusFilter === '3') {
        payload['status_filter'] = 3;
        return 'pay_to_partners';
      }
      return 'pay_won_disputes';
    }
    if (fmFilter === 5) {
      payload['transfer_from'] = filters.transfer_from;
      return 'pay_ach_payments_to_doctors_v2';
    }
    if (fmFilter === 6) {
      payload['transfer_from'] = filters.transfer_from;
      return 'pay_ach_payments_to_partners';
    }
    if (fmFilter === 7) return 'pay_contract_diff_amount';
    if (fmFilter === 8 || fmFilter === 10 || fmFilter === 11 || fmFilter === 14 || fmFilter === 15) {
      payload['transfer_from'] = filters.transfer_from;
      return 'pay_ach_payments_to_doctors';
    }
    if (fmFilter === 9) {
      return statusFilter === '1' ? 'pay_cashadjust_payments_to_partners' : 'pay_cashadjust_payments_to_doctors';
    }
    if (fmFilter === 12) return 'pay_doctor_unpaid_transfers';
    if (fmFilter === 13) return 'pay_doctor_banner_challenge';
    return 'pay_to_doctors';
  }

  private resolveFmPayAllPath(
    fmFilter: number,
    statusFilter: string,
    payload: Record<string, unknown>,
    filters: UnifiedUnpaidFilters
  ): string {
    if (fmFilter === 0) return 'finance/pay_to_doctors';
    if (fmFilter === 1) return 'finance/pay_to_partners';
    if (fmFilter === 2) return 'finance/pay_to_paul';
    if (fmFilter === 3) return 'finance/pay_guaranteed_payments';
    if (fmFilter === 4) {
      if (statusFilter === '3') {
        payload['status_filter'] = 3;
        return 'finance/pay_to_partners';
      }
      return 'finance/pay_won_disputes';
    }
    if (fmFilter === 5 || fmFilter === 8 || fmFilter === 10 || fmFilter === 11) {
      payload['transfer_from'] = filters.transfer_from;
      return 'finance/pay_ach_payments_to_doctors_v2';
    }
    if (fmFilter === 6) {
      payload['transfer_from'] = filters.transfer_from;
      return 'finance/pay_ach_payments_to_partners';
    }
    if (fmFilter === 7) return 'finance/pay_contract_diff_amount';
    if (fmFilter === 9) {
      return statusFilter === '1'
        ? 'finance/pay_cashadjust_payments_to_partners'
        : 'finance/pay_cashadjust_payments_to_doctors';
    }
    if (fmFilter === 12) return 'finance/pay_doctor_unpaid_transfers';
    if (fmFilter === 13) return 'finance/pay_doctor_banner_challenge';
    if (fmFilter === 14) return 'finance/pay_recurring_cashback';
    return 'finance/pay_to_doctors';
  }

  private resolveRecuveryPayAllPath(
    fmFilter: number,
    statusFilter: string,
    payload: Record<string, unknown>,
    filters: UnifiedUnpaidFilters
  ): string {
    if (fmFilter === 0) return 'finance/pay_to_doctors';
    if (fmFilter === 1 || fmFilter === 6) return 'finance/pay_to_partners';
    if (fmFilter === 2) return 'finance/pay_to_paul';
    if (fmFilter === 3) return 'finance/pay_guaranteed_payments';
    if (fmFilter === 4) {
      if (statusFilter === '3') {
        payload['status_filter'] = 3;
        return 'finance/pay_to_partners';
      }
      return 'finance/pay_won_disputes';
    }
    if (fmFilter === 5) {
      payload['transfer_from'] = filters.transfer_from;
      return 'finance/pay_ach_payments_to_doctors_v2';
    }
    if (fmFilter === 7) return 'finance/pay_contract_diff_amount';
    if (fmFilter === 8) {
      payload['transfer_from'] = filters.transfer_from;
      return 'finance/pay_ach_payments_to_doctors';
    }
    if (fmFilter === 9) {
      return statusFilter === '1'
        ? 'finance/pay_cashadjust_payments_to_partners'
        : 'finance/pay_cashadjust_payments_to_doctors';
    }
    if (fmFilter === 10) return 'finance/pay_doctor_unpaid_transfers';
    if (fmFilter === 11 || fmFilter === 12 || fmFilter === 13) {
      payload['dlc_type'] = this.getDlcTypeFromFmFilter(fmFilter);
      return 'finance/send_money_to_unpiad_dlc';
    }
    return 'finance/pay_to_doctors';
  }

  private resolveFmFilter(productKey: string, filters: UnifiedUnpaidFilters): number {
    return Number(filters.fm_filter ?? getDefaultFmFilter(productKey));
  }

  private getDefaultCountryId(): number {
    const fromSession = sessionStorage.getItem('country_id');
    const fromLocal = localStorage.getItem('country_id');
    const candidate = fromSession ?? fromLocal ?? '1';
    const num = Number(candidate);
    return Number.isFinite(num) ? num : 1;
  }

  /**
   * FM unpaid list APIs are called with GET; coerce numeric query fields so backends
   * that validate types (e.g. unpaid_paul_payments) receive numbers, not strings.
   */
  private normalizeFmListQueryPayload(payload: Record<string, any>): Record<string, any> {
    const numericKeys = new Set([
      'limit',
      'offset',
      'filter',
      'country_id',
      'is_vip',
      'is_orp',
      'is_orp_instant_claim',
      'status',
      'stripe_status',
      'type',
    ]);

    return Object.entries(payload).reduce<Record<string, any>>((acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc;
      }
      if (numericKeys.has(key)) {
        const num = Number(value);
        if (Number.isFinite(num)) {
          acc[key] = num;
        }
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});
  }

  private getRecuveryCountryId(): number {
    return this.getDefaultCountryId();
  }

  /** FM pay: backend expects comma-separated ids for bulk `dopa_id` (e.g. `"11362,11361"`). */
  private concatFmPaymentField(selectedPayments: UnifiedUnpaidPayment[], field: string): string | null {
    const ids = selectedPayments
      .map((payment) => (payment as Record<string, unknown>)[field])
      .filter((id) => id != null && id !== '');
    if (!ids.length) return null;
    return ids.map(String).join(',');
  }

  private setFmDopaAndContractPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    payload.dopa_id = this.concatFmPaymentField(selectedPayments, 'dopa_id');
    if (selectedPayments.length > 1) {
      const contractIds = selectedPayments.map((p) => p.contract_id).filter((id) => id != null);
      payload.contract_id = contractIds.length ? contractIds : null;
      return;
    }

    payload.contract_id = selectedPayments[0]?.contract_id ?? null;
  }

  private setFmContractPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    if (selectedPayments.length > 1) {
      payload.contract_id = selectedPayments.map((p) => p.contract_id).filter((id) => id != null);
      return;
    }

    payload.contract_id = selectedPayments[0]?.contract_id ?? null;
  }

  private setFmDopaPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    payload.dopa_id = this.concatFmPaymentField(selectedPayments, 'dopa_id');
  }

  private setFmPapaPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    payload.papa_id = this.concatFmPaymentField(selectedPayments, 'papa_id');
  }

  /** Credee pay: backend expects comma-separated ids (e.g. `"11362,11361"`). */
  private concatCredeePaymentField(selectedPayments: UnifiedUnpaidPayment[], field: string): string | null {
    const ids = selectedPayments
      .map((payment) => (payment as Record<string, unknown>)[field])
      .filter((id) => id != null && id !== '');
    if (!ids.length) return null;
    return ids.map(String).join(',');
  }

  /** Recuvery bulk pay: backend expects comma-separated ids (e.g. `"11362,11361"`). */
  private setRecuveryDopaPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    const ids = selectedPayments.map((p) => p.dopa_id).filter((id) => id != null);
    if (!ids.length) {
      payload.dopa_id = null;
      return;
    }
    payload.dopa_id = ids.length > 1 ? ids.map(String).join(',') : String(ids[0]);
  }

  private setRecuveryPapaPayload(payload: any, selectedPayments: UnifiedUnpaidPayment[]): void {
    const ids = selectedPayments.map((p) => p.papa_id).filter((id) => id != null);
    if (!ids.length) {
      payload.papa_id = null;
      return;
    }
    payload.papa_id = ids.length > 1 ? ids.map(String).join(',') : String(ids[0]);
  }

  /**
   * Backend expects `dlc_type` for `get_dlc_unpaid_transfers`.
   * Current filter mapping (V2 UI):
   * - 11: Lendee Supporter
   * - 12: Credee
   * - 13: Denefits
   *
   * Adjust values here if backend uses different numbering.
   */
  private getDlcTypeFromFmFilter(fmFilter: number): number {
    if (fmFilter === 11) return 1;
    if (fmFilter === 12) return 2;
    if (fmFilter === 13) return 3;
    return 0;
  }
}
